<template>
  <v-card>
    <v-list>
      <v-list-subheader>Connected apps</v-list-subheader>
      <v-list-item>
        <p class="text-body-medium">
          A token lets an outside tool, like an AI assistant, plan your week for
          you. Whoever has it can read your recipes and change your week plan
          and grocery list, until you revoke it.
        </p>
      </v-list-item>
      <v-list-item v-if="serverUrl">
        <v-text-field
          :model-value="serverUrl"
          label="MCP server address"
          hint="Add it as an MCP server in your AI tool, and send the token as: Authorization: Bearer <token>"
          persistent-hint
          variant="outlined"
          readonly
          class="token mt-2"
        >
          <template v-slot:append-inner>
            <v-btn
              icon="mdi-content-copy"
              variant="text"
              size="small"
              aria-label="Copy address"
              @click="copyText(serverUrl, 'Address copied')"
            />
          </template>
        </v-text-field>
      </v-list-item>
      <v-list-item
        v-for="token in tokens"
        :key="token.id"
        :title="token.name"
        :subtitle="details(token)"
        lines="two"
      >
        <template v-slot:prepend>
          <v-avatar>
            <v-icon icon="mdi-key-variant" />
          </v-avatar>
        </template>
        <template v-slot:append>
          <v-btn
            icon="mdi-delete-outline"
            variant="text"
            :aria-label="`Revoke ${token.name}`"
            @click="revokeTarget = token"
          />
        </template>
      </v-list-item>
      <v-list-item v-if="!tokens.length" subtitle="No tokens yet">
        <template v-slot:prepend>
          <v-avatar>
            <v-icon icon="mdi-key-outline" />
          </v-avatar>
        </template>
      </v-list-item>
    </v-list>
    <v-card-actions>
      <v-btn prepend-icon="mdi-key-plus" variant="tonal" @click="openCreate">
        Create token
      </v-btn>
    </v-card-actions>

    <v-dialog
      v-model="createOpen"
      max-width="480"
      :persistent="!!createdToken"
      aria-label="Create token"
    >
      <v-card>
        <v-card-title>{{
          createdToken ? 'Copy your token' : 'Create token'
        }}</v-card-title>
        <v-card-text v-if="!createdToken">
          <v-text-field
            v-model="name"
            label="Name"
            placeholder="Claude on my phone"
            hint="So you know which tool uses it"
            persistent-hint
            :counter="MAX_NAME_LENGTH"
            :maxlength="MAX_NAME_LENGTH"
            variant="outlined"
            autofocus
            @keydown.enter="create"
          />
          <v-alert v-if="error" type="error" variant="tonal" class="mt-4">
            {{ error }}
          </v-alert>
        </v-card-text>
        <v-card-text v-else>
          <p class="mb-4">
            Copy it now and paste it into the tool. You won't see it again.
          </p>
          <v-text-field
            :model-value="createdToken"
            label="Token"
            variant="outlined"
            readonly
            class="token"
          >
            <template v-slot:append-inner>
              <v-btn
                icon="mdi-content-copy"
                variant="text"
                size="small"
                aria-label="Copy token"
                @click="copyText(createdToken, 'Token copied')"
              />
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <template v-if="!createdToken">
            <v-spacer />
            <v-btn variant="text" @click="createOpen = false">Cancel</v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :disabled="!name.trim()"
              :loading="creating"
              @click="create"
            >
              Create
              <!-- Vuetify's own spinner has no accessible name -->
              <template v-slot:loader>
                <v-progress-circular
                  indeterminate
                  size="20"
                  width="2"
                  aria-label="Creating token"
                />
              </template>
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              variant="text"
              prepend-icon="mdi-content-copy"
              @click="copyText(createdToken, 'Token copied')"
            >
              Copy
            </v-btn>
            <v-spacer />
            <v-btn color="primary" variant="flat" @click="done">Done</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :model-value="!!revokeTarget"
      max-width="400"
      aria-label="Revoke token"
      @update:model-value="revokeTarget = undefined"
    >
      <v-card>
        <v-card-title>Revoke token</v-card-title>
        <v-card-text>
          Revoke <strong>{{ revokeTarget?.name }}</strong
          >? Tools using it stop working right away.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="revokeTarget = undefined">
            Cancel
          </v-btn>
          <v-btn color="error" variant="flat" @click="revoke">Revoke</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="copied" timeout="3000">{{ copiedMessage }}</v-snackbar>
  </v-card>
</template>

<script lang="ts">
  import { defineComponent, type PropType, ref } from 'vue';
  import {
    type ApiToken,
    MAX_NAME_LENGTH,
  } from '@/service/apiTokens/apiTokenClient';

  function date(millis: number): string {
    return new Date(millis).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  export default defineComponent({
    name: 'ConnectedAppsCard',
    props: {
      tokens: {
        type: Array as PropType<ApiToken[]>,
        required: true,
      },
      creating: {
        type: Boolean,
        default: false,
      },
      // The token just created, shown once
      createdToken: {
        type: String,
        default: '',
      },
      error: {
        type: String,
        default: '',
      },
      // Where outside tools connect, shown to copy
      serverUrl: {
        type: String,
        default: '',
      },
    },
    emits: {
      create: (_name: string) => true,
      revoke: (_id: string) => true,
      // The created token was seen and may be forgotten
      dismiss: () => true,
    },
    setup(props, { emit }) {
      const createOpen = ref(false);
      const name = ref('');
      const revokeTarget = ref<ApiToken>();
      const copied = ref(false);
      const copiedMessage = ref('');

      return {
        MAX_NAME_LENGTH,
        createOpen,
        name,
        revokeTarget,
        copied,
        copiedMessage,
        details(token: ApiToken): string {
          const created = token.createdAt
            ? `Created ${date(token.createdAt)}`
            : '';
          const used = token.lastUsedAt
            ? `last used ${date(token.lastUsedAt)}`
            : 'never used';
          return created ? `${created} · ${used}` : used;
        },
        openCreate() {
          name.value = '';
          createOpen.value = true;
        },
        create() {
          if (name.value.trim() && !props.creating) {
            emit('create', name.value.trim());
          }
        },
        async copyText(text: string, message: string) {
          await navigator.clipboard.writeText(text);
          copiedMessage.value = message;
          copied.value = true;
        },
        done() {
          createOpen.value = false;
          emit('dismiss');
        },
        revoke() {
          if (revokeTarget.value) {
            emit('revoke', revokeTarget.value.id);
            revokeTarget.value = undefined;
          }
        },
      };
    },
  });
</script>

<style scoped>
  .token :deep(input) {
    font-family: monospace;
  }
</style>
